import { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Editor, EditorContent } from '@tiptap/react';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { TiptapMain, TipTapWrapperDiv } from './styles';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import DropBox from '../dropBox';
import ConfirmationModal from '../modals/confirmationModal';
import Modal from '../modals/modal';
import { useEditorDragDrop, useEditorImprove, useEditorUploads, useTiptapExtensions } from './hooks';
import TiptapBubbleMenu from './tiptapBubbleMenu';
import ImproveModalPortal from './improveModal/improveModalPortal';
import ReplyMessage from '../comment/replyMessage';
import TableToolbarPortal from './tableToolbar/tableToolbarPortal';
import { TiptapEditorProps } from '../../interfaces/tiptapInterface';
import { DEFAULT_UPLOAD_FOLDER } from '../../helpers/common';

const TiptapEditor = forwardRef((props: TiptapEditorProps, ref) => {
  const {
    valueContent = '',
    setValueContent,
    placeholder,
    users,
    isAutoFocus = false,
    isEditable = true,
    disabled = false,
    isMessageInputBox = false,
    isBubbleMenuMaxWidth = false,
    isMobile = false,
    isKeyboardVisible = false,
    upload,
    dropBox,
    toolbarActions,
    reply,
    editorInstanceId
  } = props || {};

  const {
    setLoading,
    setStateLoading,
    addUploadedFiles,
    onUploadCompleted,
    uploadFolder = DEFAULT_UPLOAD_FOLDER
  } = upload || {};
  const {
    isVisible: isShowDropBox = false,
    topSpacing: applyDropBoxTopSpacing,
    width: applyDropBoxWidth,
    isTaskDescription: isTaskDescriptionDropBox = false
  } = dropBox || {};
  const { recordSnapModalOpen, handleFileInput, handleGifModal } = toolbarActions || {};
  const { replyMessage, setReplyMessage } = reply || {};

  // State variables
  const [editor, setEditor] = useState<Editor | null>(null);
  const [editorHeight, setEditorHeight] = useState(100);
  const [isUrlOpen, setIsUrlOpen] = useState(false);
  const [urlValue, setUrlValue] = useState('');

  // Mobx store variables
  const { appStore } = rootStore;
  const { themeMode } = appStore || {};

  // Extracted hooks
  const { isDragging } = useEditorDragDrop();

  const {
    isUploadBlockingModalOpen,
    isEditorFocused,
    setIsEditorFocused,
    editorInstanceIdRef,
    handleConfirmLeave,
    handleCancelLeave,
    handleFileRead,
    addImageInContent,
    addImageFileInContent
  } = useEditorUploads({
    editor,
    isTaskDescriptionDropBox,
    setValueContent,
    setLoading,
    setStateLoading,
    addUploadedFiles,
    onUploadCompleted,
    editorInstanceId: editorInstanceId || undefined,
    uploadFolder
  });

  useEffect(() => {
    if (editor) {
      const updateHeight = () => {
        const height = editor.view.dom.offsetHeight;
        setEditorHeight(height);
      };
      updateHeight();
      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(editor.view.dom);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [editor]);

  // Add snap recording card
  const addRecordingEmbedCard = useCallback(
    (url: string, recordingName: string, videoId?: string) => {
      if (editor) {
        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: 'snapEmbed',
              attrs: {
                url,
                id: videoId || '',
                recordingName,
                themeMode: themeMode?.theme || 'light'
              }
            },
            { type: 'paragraph' }
          ])
          .run();
      }
    },
    [editor, themeMode]
  );

  // Functions which are called from parent components
  useImperativeHandle(ref, () => ({
    clearValue() {
      editor?.commands.setContent('');
      if (isAutoFocus) {
        editor?.commands.focus();
      }
    },
    addImage(url: string) {
      addImageInContent(url);
    },
    addImageFile(file: File[]) {
      addImageFileInContent(file);
    },
    addContent(text: string) {
      editor?.commands.setContent(text);
    },
    addRecordingCard(firebaseUrl: string, fileName: string, videoId?: string) {
      addRecordingEmbedCard(firebaseUrl, fileName, videoId);
    },
    messageInputFocus() {
      editor?.commands.focus();
    },
    messageInputBlur() {
      editor?.commands.blur();
    },
    addContentExistingLine(content: string) {
      if (editor) {
        const { from } = editor.state.selection;
        editor.commands.insertContentAt(from, content);
        editor.commands.focus();
      }
    },
    setProjectLinkEmbed(projectLink: {
      url: string;
      projectId: string;
      projectName: string;
      projectDescription: string;
      themeMode: string;
    }) {
      if (editor) {
        editor.commands.setProjectLinkEmbed(projectLink);
      }
    },
    isFocused() {
      return isEditorFocused;
    }
  }));

  // Set mention users
  useEffect(() => {
    UserPreferenceSingleton.getInstance().setMentionUsers(users);
  }, [users]);

  // Update editor content if valueContent changes externally
  useEffect(() => {
    if (editor && valueContent !== editor.getHTML()) {
      editor.commands.setContent(valueContent, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueContent]);

  useEffect(() => {
    if (editor && isAutoFocus) {
      editor.commands.focus();
    }
  }, [editor, isAutoFocus]);

  useEffect(() => {
    if (editor && replyMessage && Object.keys(replyMessage).length > 0) {
      editor.commands.focus();
    }
  }, [editor, replyMessage]);

  const {
    isImproveModalOpen,
    setImproveModalOpen,
    isImproveLoading,
    improvedText,
    improveModalRef,
    setImproveModel,
    onClickReplace
  } = useEditorImprove(editor);

  const extensions = useTiptapExtensions({
    placeholder,
    isEditable,
    setLoading,
    setStateLoading,
    addUploadedFiles,
    recordSnapModalOpen,
    handleFileInput,
    handleGifModal,
    editorInstanceIdRef,
    handleFileRead,
    uploadFolder
  });

  // Initialize the editor instance
  useEffect(() => {
    const newEditor = new Editor({
      extensions,
      content: valueContent,
      editable: isEditable,
      onUpdate: ({ editor }) => {
        setValueContent?.(editor.getHTML());
      }
    });

    let isFocusedOnMousedown = false;

    const handleMousedown = () => {
      isFocusedOnMousedown = newEditor.isFocused;
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const closeButton = target.closest('[data-command="removeDocumentEmbed"]');
      if (closeButton) {
        const documentEmbed = closeButton.closest('.document-embed-preview-main-div');
        if (documentEmbed) {
          newEditor.state.doc.descendants((node, pos) => {
            if (node.type.name === 'documentEmbed') {
              const transaction = newEditor.state.tr.delete(pos, pos + node.nodeSize);
              newEditor.view.dispatch(transaction);
              return false;
            }
            return true;
          });
        }
      }
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');

        if (!isFocusedOnMousedown && href) {
          window.open(href, '_blank');
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const coords = { left: event.clientX, top: event.clientY };
        const posAt = newEditor.view.posAtCoords(coords);
        if (posAt) {
          newEditor.chain().setTextSelection(posAt.pos).extendMarkRange('link').run();
          const currentHref = newEditor.getAttributes('link')?.href;
          setUrlValue(currentHref || anchor.getAttribute('href') || '');
          setIsUrlOpen(true);
        }
        return;
      }

      const insideLinkMenu = !!target.closest('.link-menu');
      if (!anchor && !insideLinkMenu) {
        setIsUrlOpen(false);
      }
    };

    newEditor.view.dom.addEventListener('click', handleClick);
    newEditor.view.dom.addEventListener('mousedown', handleMousedown);

    const handleFocus = () => setIsEditorFocused(true);
    const handleBlur = () => setIsEditorFocused(false);

    newEditor.view.dom.addEventListener('focus', handleFocus, true);
    newEditor.view.dom.addEventListener('blur', handleBlur, true);

    setEditor(newEditor);

    return () => {
      newEditor.view.dom.removeEventListener('mousedown', handleMousedown);
      newEditor.view.dom.removeEventListener('click', handleClick);
      newEditor.view.dom.removeEventListener('focus', handleFocus, true);
      newEditor.view.dom.removeEventListener('blur', handleBlur, true);
      newEditor.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!editor) {
    return <div className='placeholder-editor'>{placeholder || 'Loading editor...'}</div>;
  }
  return (
    <TipTapWrapperDiv applyPosition={isDragging} isMessageInputBox={isMessageInputBox}>
      {isShowDropBox && isDragging && !disabled && (
        <DropBox
          editorHeight={isTaskDescriptionDropBox ? editorHeight - 2 : editorHeight}
          applyWidth={applyDropBoxWidth}
          applyDropBoxTopSpacing={applyDropBoxTopSpacing}
        />
      )}
      <TiptapMain isMessageInputBox={isMessageInputBox}>
        {editor && isEditable && (
          <TiptapBubbleMenu
            isBubbleMenuMaxWidth={isBubbleMenuMaxWidth}
            editor={editor}
            isImproveModalOpen={isImproveModalOpen}
            setImproveModel={setImproveModel}
            isUrlOpen={isUrlOpen}
            setIsUrlOpen={setIsUrlOpen}
            urlValue={urlValue}
            setUrlValue={setUrlValue}
          />
        )}
        {isImproveModalOpen && (
          <ImproveModalPortal
            improvedText={improvedText}
            isLoading={isImproveLoading}
            isOpen={isImproveModalOpen}
            modalRef={improveModalRef}
            onClose={() => setImproveModalOpen(false)}
            onReplace={onClickReplace}
          />
        )}
        {setReplyMessage && (!isMobile || (isMobile && isKeyboardVisible)) && (
          <ReplyMessage replyMessage={replyMessage} setReplyMessage={setReplyMessage} />
        )}
        <TableToolbarPortal editor={editor} />
        <EditorContent editor={editor} />
      </TiptapMain>
      <Modal open={isUploadBlockingModalOpen} onClose={handleCancelLeave} maxWidth={'420px'}>
        <ConfirmationModal
          modalTitle={'Confirmation'}
          description={'File is still uploading. Are you sure to leave the page?'}
          onOk={handleConfirmLeave}
          onNo={handleCancelLeave}
          okText={'Confirm'}
          cancelText={'Cancel'}
        />
      </Modal>
    </TipTapWrapperDiv>
  );
});

export default observer(TiptapEditor);
